import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CaptureConfigSection } from '@/features/capture/components/CaptureConfigSection';
import { useAuth } from '@/lib/auth/AuthProvider';
import { supabase } from '@/lib/supabase/client';
import { cn } from '@/lib/utils';

const schema = z.object({
  display_name: z.string().min(1, 'El nombre es requerido').max(60, 'Maximo 60 caracteres'),
  avatar_url: z
    .string()
    .url('Debe ser una URL valida')
    .or(z.literal(''))
    .nullish()
    .transform((v) => v ?? ''),
});

type Values = z.infer<typeof schema>;

export const Route = createFileRoute('/_app/$wsSlug/profile')({
  component: ProfilePage,
});

const myProfileQueryKey = (userId: string) => ['profile', userId, 'preferences'] as const;

function ProfilePage() {
  const { user } = useAuth();
  const userId = user?.id ?? '';
  const qc = useQueryClient();
  const meta = (user?.user_metadata ?? {}) as {
    display_name?: string;
    avatar_url?: string;
  };

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      display_name: meta.display_name ?? user?.email ?? '',
      avatar_url: meta.avatar_url ?? '',
    },
  });

  const { data: prefs } = useQuery({
    queryKey: myProfileQueryKey(userId),
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('notify_when_supervisor')
        .eq('id', userId)
        .maybeSingle();
      if (error) throw error;
      return data ?? { notify_when_supervisor: true };
    },
    staleTime: 60_000,
  });

  const onSubmit = form.handleSubmit(async (values) => {
    const { error } = await supabase.auth.updateUser({
      data: {
        display_name: values.display_name,
        avatar_url: values.avatar_url || null,
      },
    });
    if (error) {
      toast.error(error.message);
      return;
    }

    // Sincronizar la fila en public.profiles
    // SK-001: chequear error explicitamente; antes el resultado se descartaba
    // y el toast 'Perfil actualizado' se disparaba aunque el UPDATE fallara.
    const { error: profileErr } = await supabase
      .from('profiles')
      .update({
        display_name: values.display_name,
        avatar_url: values.avatar_url || null,
      })
      .eq('id', userId);

    if (profileErr) {
      toast.error('No se pudo actualizar el perfil', { description: profileErr.message });
      return;
    }

    toast.success('Perfil actualizado');
  });

  async function setNotifyWhenSupervisor(next: boolean) {
    if (!userId) return;
    const { error } = await supabase
      .from('profiles')
      .update({ notify_when_supervisor: next })
      .eq('id', userId);
    if (error) {
      toast.error(error.message);
      return;
    }
    void qc.invalidateQueries({ queryKey: myProfileQueryKey(userId) });
    toast.success(
      next
        ? 'Recibirás notificaciones de tareas donde eres supervisor'
        : 'Notificaciones de supervisor silenciadas',
    );
  }

  const supervisorNotify = prefs?.notify_when_supervisor ?? true;

  return (
    <div className="max-w-lg mx-auto p-6 space-y-8">
      <div>
        <h1 className="text-xl font-semibold text-ink">Mi perfil</h1>
        <p className="mt-0.5 text-sm text-quiet">
          Tu perfil y preferencias de notificación. Cambios afectan solo a tu vista de la
          plataforma.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        {/* Email — solo lectura */}
        <div className="space-y-1">
          <Label>Email</Label>
          <div className="text-sm text-body px-1">{user?.email}</div>
        </div>

        {/* Nombre */}
        <div className="space-y-1">
          <Label htmlFor="display_name">Nombre</Label>
          <Input
            id="display_name"
            placeholder="Tu nombre completo"
            {...form.register('display_name')}
            aria-invalid={!!form.formState.errors.display_name}
          />
          {form.formState.errors.display_name ? (
            <p className="text-xs text-critical">{form.formState.errors.display_name.message}</p>
          ) : null}
        </div>

        {/* Avatar URL */}
        <div className="space-y-1">
          <Label htmlFor="avatar_url">URL de avatar</Label>
          <Input
            id="avatar_url"
            type="url"
            placeholder="https://..."
            {...form.register('avatar_url')}
            aria-invalid={!!form.formState.errors.avatar_url}
          />
          <p className="text-xs text-mute">
            URL pública a tu foto. Déjalo vacío para usar la inicial de tu nombre.
          </p>
          {form.formState.errors.avatar_url ? (
            <p className="text-xs text-critical">{form.formState.errors.avatar_url.message}</p>
          ) : null}
        </div>

        <Button type="submit" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? 'Guardando...' : 'Guardar'}
        </Button>
      </form>

      <div className="border-t border-border-soft pt-6 space-y-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">Notificaciones</h2>
          <p className="text-xs text-quiet mt-0.5">
            Controla qué eventos te llegan al inbox y a Telegram.
          </p>
        </div>

        <div className="flex items-start justify-between gap-4 rounded-md border border-border bg-surface-elev p-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink">Notificaciones cuando soy supervisor</p>
            <p className="text-xs text-quiet mt-0.5">
              Cuando está apagado, no recibes asignaciones, comentarios ni cambios de estado de
              tareas donde eres supervisor — solo de las que eres ejecutor.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={supervisorNotify}
            onClick={() => void setNotifyWhenSupervisor(!supervisorNotify)}
            className={cn(
              'relative inline-flex h-5 w-9 shrink-0 mt-1 cursor-pointer rounded-full border-2 border-transparent transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent-sf)]',
              supervisorNotify ? 'bg-[color:var(--accent)]' : 'bg-border',
            )}
          >
            <span
              className={cn(
                'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-card transition-transform',
                supervisorNotify ? 'translate-x-4' : 'translate-x-0',
              )}
            />
          </button>
        </div>
      </div>

      <CaptureConfigSection />
    </div>
  );
}
